// TODO: move receiving segments to the debounced search or to the background
import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, Grid } from '@mui/material';
import { Form } from 'formik';
import { segmentsApi } from 'components/audience/segmentsApi';
import {
    MESSAGE_CUSTOMER_AUDIENCE_UPDATE_ERROR,
    MESSAGE_CUSTOMER_AUDIENCE_UPDATE_SUCCESS
} from 'config/messages';
import { MuiForm } from 'lib/Form';
import { MultiSelectFormField } from 'lib/form/fields/MultiSelectFormField';
import { enqueueSnackbar } from 'store/notifications/notificationsActions';
import { Customer, CustomerSegment } from '../models/Customer';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { Row } from 'ui/Flex';

interface CustomersAudienceEditCardProps {
    customer: Customer;
    onClose: () => void;
    onChange: (segments: CustomerSegment[]) => void;
}

export const CustomersAudienceEditCard: React.FC<CustomersAudienceEditCardProps> = props => {
    const { onClose, customer, onChange } = props;
    const [loading, setLoading] = React.useState(true);
    const [submitting, setSubmitting] = React.useState(false);
    const [allSegments, setAllSegments] = React.useState<{ _id: string; title: string }[]>([]);
    const dispatch = useDispatch();

    React.useEffect(() => {
        const fetchSegments = async () => {
            try {
                const segments = (await segmentsApi.getAll()).map(
                    (segment: { _id: string; title: string }) => ({
                        _id: segment._id,
                        title: segment.title
                    })
                );
                setAllSegments(segments);
                setLoading(false);
            } catch (error) {
                dispatch(enqueueSnackbar(error.message, { variant: 'error' }));
                setLoading(false);
            }
        };
        fetchSegments();
    }, [dispatch]);

    const segmentOptions = React.useMemo(
        () => allSegments.map(segment => ({ label: segment.title, value: segment._id })),
        [allSegments]
    );

    const initialValues = React.useMemo(
        () => ({
            segments: (customer?.segments || []).reduce(
                (acc: { label: string; value: string }[], segment) => {
                    if (segment.included) {
                        acc.push({ label: segment.title, value: segment._id });
                    }
                    return acc;
                },
                []
            )
        }),
        [customer?.segments]
    );

    const handleSubmit = React.useCallback(
        async (data: { segments?: { label: string; value: string }[] }) => {
            setSubmitting(true);
            const segments = data.segments || [];
            const currentSegmentIds = new Set(
                (customer?.segments || []).reduce((acc: string[], segment) => {
                    if (segment.included) {
                        acc.push(segment._id);
                    }
                    return acc;
                }, [])
            );
            const newSegmentIds = new Set(segments.map(segment => segment.value));
            const removedSegmentIds = Array.from(currentSegmentIds).filter(x => !newSegmentIds.has(x));
            const addedSegmentIds = Array.from(newSegmentIds).filter(x => !currentSegmentIds.has(x));

            // Check if there have been any changes
            if (removedSegmentIds.length === 0 && addedSegmentIds.length === 0) {
                setSubmitting(false);
                return onClose();
            }

            // For each added Segment Id make request
            const responses = await Promise.all([
                ...addedSegmentIds.map(segmentId => segmentsApi.addToSegment(segmentId, [customer._id])),
                ...removedSegmentIds.map(segmentId => segmentsApi.removeFromSegment(segmentId, customer._id))
            ]);

            // If all requests are successful then success, else error
            let requestsSuccessful = true;
            for (const response of responses) {
                if (!response.ok) {
                    requestsSuccessful = false;
                    break;
                }
            }

            if (!requestsSuccessful) {
                setSubmitting(false);
                dispatch(enqueueSnackbar(MESSAGE_CUSTOMER_AUDIENCE_UPDATE_ERROR, { variant: 'error' }));
                onClose();
                return;
            }

            const newSegments = allSegments.map(segment => ({
                _id: segment._id,
                title: segment.title,
                included: newSegmentIds.has(segment._id)
            }));
            setSubmitting(false);
            dispatch(enqueueSnackbar(MESSAGE_CUSTOMER_AUDIENCE_UPDATE_SUCCESS, { variant: 'success' }));
            onChange(newSegments);
            onClose();
        },
        [customer?._id, customer?.segments, allSegments, dispatch, onChange, onClose]
    );

    return (
        <MuiForm onSubmit={handleSubmit} initialValues={initialValues}>
            {({ submitForm, isValid, isSubmitting }) => (
                <Form>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <MultiSelectFormField
                                name="segments"
                                label=""
                                description="Select audiences you want to add a user to or remove them from."
                                disabled={loading}
                                options={segmentOptions}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Row gutter align="flex-end">
                                <Button onClick={onClose} variant="outlined">
                                    Cancel
                                </Button>
                                <LoadingButton
                                    onClick={submitForm}
                                    disabled={!isValid || isSubmitting || loading}
                                    loading={submitting || isSubmitting}
                                    variant="contained"
                                >
                                    Save
                                </LoadingButton>
                            </Row>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </MuiForm>
    );
};
